import type { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HrefService, NotificationService } from '@technocom/common';
import { EnvironmentService, ServerRequestInterceptorService } from '@technocom/core';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { catchError, of, switchMap, throwError } from 'rxjs';
import type { CaptchaResponse } from '@app/core/services/captcha.service';
import { CaptchaService } from '@app/core/services/captcha.service';
import { SecurityService } from '@technocom/auth';
import { HrefUtils } from '@technocom/common/http';
import { TranslocoService } from '@ngneat/transloco';
import { Role } from '@app/shared/models/user/role';

@Injectable({
  providedIn: 'root',
})
export class BackendInterceptorService extends ServerRequestInterceptorService {
  protected route = inject(Router);
  protected notify = inject(NotificationService);
  protected href = inject(HrefService);
  protected captchaService = inject(CaptchaService);
  protected env = inject<EnvironmentService>(EnvironmentService);
  protected localize = inject(TranslocoService);
  protected securityService = inject(SecurityService);
  // Реестр курсов - тоже закрытый раздел: префикс /operation/course добавлять
  // нельзя, иначе получится /operation/operation/course. Роль Лектора (6) не
  // входит в проверку canUserAccess([OPR, DATA_ADM]) ниже, поэтому для него
  // этот префикс считался бы публичным и запрос ушёл бы в /public/...
  protected lockedList = ['authentification', 'log', 'statistic', 'person', 'operation/course'];

  intercept(request: HttpRequest<any>, chain: HttpHandler): Observable<HttpEvent<any>> {
    // перехватываем запросы на отправку смс и добавляем прохождение капчи
    if (request.url.includes('send-code')) {
      const dialog = this.captchaService.showCaptchaDialog();

      return dialog.result.pipe(
        switchMap((result: CaptchaResponse) => {
          let req: HttpRequest<any>;

          if (result?.code) {
            req = request.clone({ setParams: { captchaValue: result.code, captchaId: result.captchaId } });

            return super.intercept(req ?? request, chain).pipe(
              catchError(error => {
                const errorStatus = error instanceof HttpErrorResponse ? error.status || HttpStatusCode.NotFound : null;

                if (
                  errorStatus === (HttpStatusCode.Forbidden as number) ||
                  errorStatus === (HttpStatusCode.NotAcceptable as number) ||
                  errorStatus === (HttpStatusCode.RequestTimeout as number)
                ) {
                  this.notify.warn('Попробуйте еще раз', `Капча не пройдена`);

                  return of(null);
                }

                return throwError(() => error);
              }),
            );
          }

          return of(null);
        }),
      );
    }
    const publicPath = this.env.get('publicPath');
    const securePath = this.env.get('securePath');

    const forcePublic = request.url.startsWith(publicPath);
    const forceOperation = request.url.startsWith(securePath);

    const isLocked = this.lockedList.some(i => request.url.includes(i));

    if (!isLocked && !forceOperation && !forcePublic) {
      if (request.method.toLowerCase() === 'get' && !request.params.has('lang')) {
        request.params.set('lang', this.localize.getActiveLang().toUpperCase());
      }

      if (request.url.includes('find') && !request.params.has('actualOnly')) {
        request.params.set('actualOnly', !this.securityService.canUserAccess([Role.OPR, Role.DATA_ADM]));
      }

      const url = HrefUtils.join(
        this.securityService.canUserAccess([Role.OPR, Role.DATA_ADM]) ? securePath : publicPath,
        request.url,
      );

      return super.intercept(request.clone({ url }), chain);
    }

    return super.intercept(request, chain);
  }
}
